import { wrappedApiGet } from 'utils/http';
import {
  ApiResponseError,
  HttpUnauthorizedError,
  PayerError,
  ServerResponseJsonSyntaxError,
} from '@payer-admin-ui/components/src/utils/errors';
import { AiChatRequest } from '@payer-admin-ui/api/src/ai/requests/AiChatRequest';
import type { AiChatAttachmentsUploadResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatAttachmentsUploadResponse';
import { AiChatResponse, AiChatResponseStatus } from '@payer-admin-ui/api/src/ai/responses/AiChatResponse';
import type { AiChatConversationMessageResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatConversationResponse';
import { AiChatConversationResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatConversationResponse';
import { AiChatConversationSummaryResponse } from '@payer-admin-ui/api/src/ai/responses/AiChatConversationSummaryResponse';

type AiChatStreamEvent =
  | {
      type: 'OPEN';
      conversationId: string;
      title?: string | null;
    }
  | {
      type: 'MESSAGE';
      conversationId: string;
      message: AiChatConversationMessageResponse;
      title?: string | null;
    }
  | {
      type: 'DONE';
      conversationId: string;
      status: AiChatResponseStatus;
      info?: string | null;
      title?: string | null;
    };

type AiChatStreamHandlers = {
  onOpen?: (event: Extract<AiChatStreamEvent, { type: 'OPEN' }>) => void;
  onMessage?: (event: Extract<AiChatStreamEvent, { type: 'MESSAGE' }>) => void;
  onDone?: (event: Extract<AiChatStreamEvent, { type: 'DONE' }>) => void;
};

const ISE_MESSAGE = 'If the problem persists, please contact Payer.';

const parseEventStreamLine = (rawLine: string) => {
  const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;

  if (line === '') return { kind: 'event_end' as const };
  if (line.startsWith(':')) return { kind: 'comment' as const };

  if (line.startsWith('event:')) {
    return { kind: 'event' as const, value: line.slice('event:'.length).trim() };
  }

  if (line.startsWith('data:')) {
    return { kind: 'data' as const, value: line.slice('data:'.length).replace(/^ /, '') };
  }

  return { kind: 'other' as const };
};

const throwForNonOkResponse = async (response: Response) => {
  if (response.status === 401) throw new HttpUnauthorizedError();

  const contentType = response.headers.get('Content-Type');
  const responseText = await response.text();

  if (response.status === 504) throw new PayerError({ code: 'serverUnavailable' });

  if (contentType && contentType.includes('application/json')) {
    let responseError: { code?: string; detail?: string; message?: string } | null = null;
    try {
      responseError = JSON.parse(responseText) as { code?: string; detail?: string; message?: string };
    } catch (error) {
      throw new ServerResponseJsonSyntaxError({ responseText, cause: error });
    }

    if (responseError?.code || responseError?.message || responseError?.detail) {
      if (response.status !== 400 && response.status !== 404 && !responseError?.code) {
        throw new ApiResponseError({ message: ISE_MESSAGE });
      }
      throw new ApiResponseError({
        code: responseError?.code,
        message: responseError?.message,
        detail: responseError?.detail,
      });
    }
  }

  throw new PayerError({
    code: 'serverResponseNotJson',
    detail: `status: ${response.statusText} response: '${responseText}'`,
  });
};

const parseJsonResponseBody = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get('Content-Type');
  const responseText = await response.text();

  if (contentType && contentType.includes('application/json')) {
    try {
      return JSON.parse(responseText) as T;
    } catch (error) {
      throw new ServerResponseJsonSyntaxError({ responseText, cause: error });
    }
  }

  throw new PayerError({
    code: 'serverResponseNotJson',
    detail: `status: ${response.statusText} response: '${responseText}'`,
  });
};

const uploadAiChatAttachments = async (
  {
    conversationId,
    files,
  }: {
    conversationId: string;
    files: File[];
  },
  { signal }: { signal?: AbortSignal } = {},
): Promise<AiChatAttachmentsUploadResponse> => {
  const body = new FormData();
  body.append('conversationId', conversationId);
  files.forEach(file => {
    body.append('files', file, file.name);
  });

  const response = await fetch('/merchant-admin/api/v1/ai/chat_attachments', {
    method: 'POST',
    body,
    credentials: 'include',
    signal,
  });

  if (!response.ok) await throwForNonOkResponse(response);

  return parseJsonResponseBody<AiChatAttachmentsUploadResponse>(response);
};

const deleteAiChatAttachment = async (
  {
    attachmentId,
  }: {
    attachmentId: string;
  },
  { signal }: { signal?: AbortSignal } = {},
): Promise<void> => {
  const response = await fetch(`/merchant-admin/api/v1/ai/chat_attachments/${encodeURIComponent(attachmentId)}`, {
    method: 'DELETE',
    credentials: 'include',
    signal,
  });

  if (!response.ok) await throwForNonOkResponse(response);
};

const consumeAiChatStream = async (
  request: AiChatRequest,
  {
    signal,
    handlers,
  }: {
    signal?: AbortSignal;
    handlers?: AiChatStreamHandlers;
  } = {},
): Promise<AiChatResponse> => {
  const response = await fetch('/merchant-admin/api/v1/ai/chat_stream', {
    method: 'POST',
    headers: {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
    credentials: 'include',
    signal,
  });

  if (!response.ok) await throwForNonOkResponse(response);
  if (!response.body) throw new PayerError({ code: 'serverResponseNotJson' });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  let buffer = '';
  let eventName: string | null = null;
  let dataLines: string[] = [];

  let conversationId = request.conversationId?.trim() || null;
  let title: string | null | undefined = undefined;
  let status: AiChatResponseStatus | null = null;
  let info: string | null | undefined = undefined;

  const flushEvent = () => {
    if (!eventName) return;
    const data = dataLines.join('\n');
    if (!data) return;

    let parsed: AiChatStreamEvent;
    try {
      parsed = JSON.parse(data) as AiChatStreamEvent;
    } catch (error) {
      throw new ServerResponseJsonSyntaxError({ responseText: data, cause: error });
    }

    if (parsed.title !== undefined) title = parsed.title;
    if (!conversationId && parsed.conversationId) conversationId = parsed.conversationId;

    if (eventName === 'open' && parsed.type === 'OPEN') handlers?.onOpen?.(parsed);
    if (eventName === 'message' && parsed.type === 'MESSAGE') handlers?.onMessage?.(parsed);
    if (eventName === 'done' && parsed.type === 'DONE') {
      status = parsed.status;
      info = parsed.info;
      handlers?.onDone?.(parsed);
    }
  };

  let isDoneReadingStream = false;
  while (!isDoneReadingStream) {
    const { value, done } = await reader.read();
    if (done) {
      isDoneReadingStream = true;
      continue;
    }

    buffer += decoder.decode(value, { stream: true });

    let hasMoreCompleteLines = true;
    while (hasMoreCompleteLines) {
      const newLineIndex = buffer.indexOf('\n');
      if (newLineIndex === -1) {
        hasMoreCompleteLines = false;
        continue;
      }

      const rawLine = buffer.slice(0, newLineIndex);
      buffer = buffer.slice(newLineIndex + 1);

      const parsedLine = parseEventStreamLine(rawLine);

      if (parsedLine.kind === 'event') eventName = parsedLine.value || null;
      if (parsedLine.kind === 'data') dataLines.push(parsedLine.value);

      if (parsedLine.kind === 'event_end') {
        if (eventName && dataLines.length > 0) flushEvent();
        eventName = null;
        dataLines = [];
      }

      if (status) hasMoreCompleteLines = false;
    }

    if (status) isDoneReadingStream = true;
  }

  if (status) {
    try {
      await reader.cancel();
    } catch (error) {
      void error;
    }
  }

  if (!status) throw new PayerError({ code: 'serverResponseNotJson' });

  const result: AiChatResponse = {
    conversationId: conversationId ?? request.conversationId ?? '',
    messages: [],
    status,
  };

  if (title !== undefined) result.title = title;
  if (info !== undefined) result.info = info;

  return result;
};

const aiAPI = {
  chat: {
    chat: async (
      request: AiChatRequest,
      { signal, handlers }: { signal?: AbortSignal; handlers?: AiChatStreamHandlers } = {},
    ): Promise<AiChatResponse> => {
      return consumeAiChatStream(request, { signal, handlers });
    },
    uploadAttachments: async (
      { conversationId, files }: { conversationId: string; files: File[] },
      { signal }: { signal?: AbortSignal } = {},
    ): Promise<AiChatAttachmentsUploadResponse> => {
      return uploadAiChatAttachments({ conversationId, files }, { signal });
    },
    deleteAttachment: async (attachmentId: string, { signal }: { signal?: AbortSignal } = {}): Promise<void> => {
      return deleteAiChatAttachment({ attachmentId }, { signal });
    },
    listConversations: async (limit = 50): Promise<AiChatConversationSummaryResponse[]> => {
      return wrappedApiGet<AiChatConversationSummaryResponse[]>('/v1/ai/chats', {
        query: { limit },
      }).withObjectResponse();
    },
    getConversation: async (conversationId: string): Promise<AiChatConversationResponse> => {
      return wrappedApiGet<AiChatConversationResponse>(`/v1/ai/chats/${conversationId}`).withObjectResponse();
    },
  },
};

export { aiAPI };
